import {
    html,
    css,
    type ElementStyles,
    FASTElement,
    attr,
    nullableNumberConverter,
} from '@microsoft/fast-element';
import { PropertyIcons as PropertyIconEnum } from 'owa-mail-list-actions';
import {
    AttachRegular,
    MentionRegular,
    LinkRegular,
    ClockRegular,
    ArrowReplyRegular,
    ArrowForwardRegular,
    LockClosedRegular,
    RibbonRegular,
    CalendarEmptyRegular,
    CalendarCheckmarkRegular,
    CalendarCancelRegular,
    CalendarQuestionMarkRegular,
    CalendarEyeRegular,
    VoicemailRegular,
    ArrowDownRegular,
    ImportantRegular,
    CheckboxCheckedRegular,
    CalendarSyncRegular,
    NotepadRegular,
    NoteRegular,
    ArrowCircleRightRegular,
    DoorArrowLeftRegular,
    CheckmarkCircleRegular,
    MailDismissRegular,
    CheckmarkRegular,
    ClockAlarmRegular,
    CalendarPersonRegular,
    AlertRegular,
    PinFilled,
    FlagFilled,
    VoicemailArrowBackRegular,
    VoicemailArrowForwardRegular,
    VoicemailSubtractRegular,
    FaxRegular,
    ContactCardRegular,
    NotePinRegular,
    MailWarningRegular,
} from '@fluentui/react-icons/lib/fonts';
import { iconBarRegistry } from '../customElementRegistry';

const template = html<PropertyIcon>`${x => getPropertyIconConfig(x.icon).icon}`;

const baseStyles = css`
    :host {
        align-items: center;
        color: var(--neutralPrimaryAlt);
        cursor: default;
        display: var(--propertyIconDisplay, flex);
        font-family: FluentSystemIcons;
        font-size: 16px;
        font-weight: 100;
        height: 24px;
        justify-content: center;
        margin: 0px;
        padding-left: 0px;
        text-align: center;
        width: 24px;
    }

    :host(.full) {
        font-size: 20px;
    }

    :host(.denseless) {
        font-size: 20px;
    }

    :host(.alwaysShowPropertyIcons) {
        display: flex;
    }

    :host(.iconNoDisplay) {
        display: none;
    }

    @media screen and (forced-colors: active) {
        :host {
            background-color: HighlightText;
            color: CanvasText;

            &:hover {
                background-color: HighlightText;
            }
        }
    }
`;

const mentionedMeStyles = css`
    :host {
        color: var(--themeDark) !important;
    }
`;

const importantStyles = css`
    :host {
        color: var(--redDark) !important;
    }
`;

const snoozeStyles = css`
    :host {
        color: var(--green20) !important;
    }
`;

const pinnedStyles = css`
    :host {
        color: var(--customBlueForeground1) !important;
    }
`;

const flaggedStyles = css`
    :host {
        color: var(--colorStatusDangerForeground1) !important;
    }
`;

const hiddenStyles = css`
    :host {
        visibility: hidden;
    }
`;

function getPropertyIconConfig(propertyIcon: PropertyIconEnum): {
    icon: string;
    styles?: ElementStyles;
} {
    switch (propertyIcon) {
        case PropertyIconEnum.GlobalMentionedMe:
            return {
                icon: MentionRegular.codepoint,
                // TODO: Depends on pinned state
                styles: mentionedMeStyles,
            };
        case PropertyIconEnum.Attachment:
            return {
                icon: AttachRegular.codepoint,
            };
        case PropertyIconEnum.ImportanceLow:
            return {
                icon: ArrowDownRegular.codepoint,
            };
        case PropertyIconEnum.ImportanceHigh:
            return {
                styles: importantStyles,
                icon: ImportantRegular.codepoint,
            };
        case PropertyIconEnum.Snooze:
            return {
                styles: snoozeStyles,
                icon: ClockRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewReplied:
            return {
                icon: ArrowReplyRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewForwarded:
            return {
                icon: ArrowForwardRegular.codepoint,
            };
        case PropertyIconEnum.LockClosedRegular:
        case PropertyIconEnum.MailListViewIRM:
        case PropertyIconEnum.MailListViewIRMReplied:
        case PropertyIconEnum.MailListViewIRMForwarded:
            return {
                icon: LockClosedRegular.codepoint,
            };
        case PropertyIconEnum.RibbonRegular:
            return {
                icon: RibbonRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewVoiceMailItem:
            return {
                icon: VoicemailRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewVoiceMailReplied:
            return {
                icon: VoicemailArrowBackRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewVoiceMailForwarded:
            return {
                icon: VoicemailArrowForwardRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewVoiceMailIRM:
            return {
                icon: VoicemailSubtractRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewFaxItem:
            return {
                icon: FaxRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentItem:
            return {
                icon: CalendarEmptyRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentDelegate:
            return {
                icon: CalendarPersonRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentAccepted:
            return {
                icon: CalendarCheckmarkRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentDeclined:
            return {
                icon: CalendarCancelRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentTentative:
            return {
                icon: CalendarQuestionMarkRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentFollow:
            return {
                icon: CalendarEyeRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewAppointmentCanceled:
            return {
                icon: CalendarCancelRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewContactItem:
            return {
                icon: ContactCardRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewTaskItem:
            return {
                icon: CheckboxCheckedRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewTaskReocurItem:
            return {
                icon: CalendarSyncRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewJournalItem:
            return {
                icon: NotepadRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewPostItem:
            return {
                icon: NotePinRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewNoteItem:
            return {
                icon: NoteRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewDelivery:
            // Bug 1752 - culture specific
            return {
                icon: ArrowCircleRightRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewNonDelivery:
        case PropertyIconEnum.MailListViewApprovalRequest:
        case PropertyIconEnum.MailListViewResponseApprove:
        case PropertyIconEnum.MailListViewResponseReject:
            // add sprite response_reject.png
            return {
                icon: MailWarningRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewOutOfOffice:
            // Bug 1752 - culture specific
            return {
                icon: DoorArrowLeftRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewReadReport:
            return {
                icon: CheckmarkCircleRegular.codepoint,
            };
        case PropertyIconEnum.MailListViewReminderMessage:
            return {
                icon: AlertRegular.codepoint,
            };
        case PropertyIconEnum.Link:
            return {
                icon: LinkRegular.codepoint,
            };
        case PropertyIconEnum.MailDismiss:
            return {
                icon: MailDismissRegular.codepoint,
            };
        case PropertyIconEnum.Pinned:
            return {
                icon: PinFilled.codepoint,
                styles: pinnedStyles,
            };
        case PropertyIconEnum.Flagged:
            return {
                icon: FlagFilled.codepoint,
                styles: flaggedStyles,
            };
        case PropertyIconEnum.Completed:
            return { icon: CheckmarkRegular.codepoint };
        case PropertyIconEnum.Reminder:
            return { icon: ClockAlarmRegular.codepoint };
        default:
            return {
                icon: '',
                styles: hiddenStyles,
            };
    }
}

class PropertyIcon extends FASTElement {
    // eslint-disable-next-line owa-custom-rules/no-side-effects -- Web component decorator
    @attr({ converter: nullableNumberConverter })
    icon!: PropertyIconEnum;

    connectedCallback(): void {
        super.connectedCallback();
        const { styles } = getPropertyIconConfig(this.icon);
        this.$fastController.addStyles(styles);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        const { styles } = getPropertyIconConfig(this.icon);
        this.$fastController.removeStyles(styles);
    }

    iconChanged(oldValue: PropertyIconEnum, newValue: PropertyIconEnum): void {
        const { styles: oldStyles } = getPropertyIconConfig(oldValue);
        this.$fastController.removeStyles(oldStyles);

        const { styles: newStyles } = getPropertyIconConfig(newValue);
        this.$fastController.addStyles(newStyles);
    }
}

// eslint-disable-next-line owa-custom-rules/no-side-effects -- Web components
PropertyIcon.define({
    name: 'property-icon',
    template,
    styles: baseStyles,
    registry: iconBarRegistry,
});

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'property-icon': React.DetailedHTMLProps<React.HTMLProps<HTMLElement>, PropertyIcon> & {
                icon: PropertyIconEnum;
                class?: string;
            };
        }
    }
}
